const HTML_ESCAPE_MAP: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
};

const escapeHtml = (value: string): string =>
    value.replaceAll(/[&<>"']/g, (character) => HTML_ESCAPE_MAP[character]);

/**
 * Plain-text draft body -> composer HTML. Breaks use `<br><br>` not `<p>`: the
 * composer zeroes paragraph margins (`.ProseMirror p { margin: 0 }`), so adjacent
 * `<p>` collapse and blank lines vanish. Plain-text adapters MUST call this.
 */
export const plainTextToComposerHtml = (text: string): string => {
    if (!text) {
        return '';
    }

    const paragraphs = text
        .replaceAll(/\r\n?/g, '\n')
        .split(/\n{2,}/)
        .map((paragraph) => paragraph.trim())
        .filter((paragraph) => paragraph !== '');

    if (paragraphs.length === 0) {
        return '';
    }

    const inner = paragraphs
        .map((paragraph) => paragraph.split('\n').map(escapeHtml).join('<br>'))
        .join('<br><br>');

    return `<p>${inner}</p>`;
};

// Block-level tags become blank-line-separated paragraphs so lists don't mash into prose.
const PARAGRAPH_BLOCK_TAGS = new Set([
    'P',
    'DIV',
    'BLOCKQUOTE',
    'UL',
    'OL',
    'H1',
    'H2',
    'H3',
    'H4',
    'H5',
    'H6',
]);

const nodeToPlainText = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent ?? '';
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
        return '';
    }

    const element = node as Element;

    if (element.tagName === 'BR') {
        return '\n';
    }

    const inner = Array.from(element.childNodes).map(nodeToPlainText).join('');

    // Keep a link's href — it carries intent (a booking/offer URL) the model must see.
    if (element.tagName === 'A') {
        const href = (element.getAttribute('href') ?? '').trim();
        const text = inner.trim();

        if (href && text && href !== text) {
            return `${inner} (${href})`;
        }

        return href && !text ? href : inner;
    }

    // One list item per line; flatten the inner <p> Tiptap wraps each item in.
    if (element.tagName === 'LI') {
        return `\n${inner.replace(/^\n+/, '').replace(/\n+$/, '')}`;
    }

    if (PARAGRAPH_BLOCK_TAGS.has(element.tagName)) {
        return `\n\n${inner}\n\n`;
    }

    return inner;
};

/**
 * Inverse of {@link plainTextToComposerHtml}. Update feeds this into the model
 * prompt as plain text; raw HTML would be echoed back as literal tags, so the
 * composable MUST run the body through this before update.
 */
export const composerHtmlToPlainText = (html: string): string => {
    if (!html) {
        return '';
    }

    const container = document.createElement('div');
    container.innerHTML = html;

    return Array.from(container.childNodes)
        .map(nodeToPlainText)
        .join('')
        .replaceAll(/\r\n?/g, '\n')
        .replaceAll(/[ \t]+\n/g, '\n')
        .replaceAll(/\n{3,}/g, '\n\n')
        .trim();
};
